/**
 * Send a connection-authenticated request and recover once from a 401.
 *
 * A 401 on a cookie session that was known-good joins the shared auth
 * recovery flight (ato-pwa#352): the person re-authenticates in place and a
 * GET/HEAD request is replayed exactly once. Mutations are never replayed —
 * this layer cannot prove the server did not apply the first attempt.
 *
 * Lives in its own module, not auth.ts, so the static edge to authRecovery
 * cannot become a cycle: authRecovery depends on auth.ts and
 * resolveProfile.ts, and it never reaches back here.
 *
 * The request fires synchronously within the call tick — the same dispatch
 * timing as the fetch(url, withConnectionAuth(init)) it replaces. That
 * matters: an import POST constructed under one account must not sit
 * unissued while a sign-out/sign-in lands in between (libraryClient's
 * account-switch regression test measures exactly this window).
 */
import { fetchWithAuthRecovery } from "../../auth/authRecovery";
import { withConnectionAuth } from "./auth";

export function fetchWithConnectionAuth(
  input: RequestInfo | URL,
  init: RequestInit = {},
  fetchImpl: typeof fetch = (...args) => globalThis.fetch(...args),
): Promise<Response> {
  return fetchWithAuthRecovery(
    () => fetchImpl(input, withConnectionAuth(init)),
    init.method ?? "GET",
  );
}
